import type {
  ProductExpiryFilter,
  ProductPlacementFilter,
} from "../../../api/WarehouseApi";
import { Measures } from "../../../types/entities";
import { formatTypeTag } from "../../../utils/typeTags";

export interface ProductsListFilterChip {
  key: string;
  label: string;
  onClick: () => void;
}

export interface ProductsListFilterState {
  placementFilter: ProductPlacementFilter;
  expiryFilter: ProductExpiryFilter;
  expiringDays: number;
  measureFilter: "all" | Measures;
  queryFilter: string;
  typeTagFilter: string;
}

export function buildProductsListRequestKey(
  state: ProductsListFilterState,
): string {
  return [
    state.placementFilter,
    state.expiryFilter,
    String(state.expiringDays),
    state.measureFilter,
    state.queryFilter,
    state.typeTagFilter,
  ].join("|");
}

export function buildProductsListPageTitle(input: {
  explicitTitle?: string;
  filters: ProductsListFilterState;
}): string {
  if (input.explicitTitle?.trim()) {
    return input.explicitTitle;
  }

  if (input.filters.typeTagFilter) {
    return `Товары по тегу ${formatTypeTag(input.filters.typeTagFilter)}`;
  }

  if (input.filters.expiryFilter === "expired") {
    return "Просроченные товары";
  }

  if (input.filters.expiryFilter === "expiringSoon") {
    return "Товары с истекающим сроком";
  }

  if (input.filters.placementFilter === "withoutBox") {
    return "Товары без коробки";
  }

  if (input.filters.placementFilter === "inBox") {
    return "Товары в коробках";
  }

  if (input.filters.measureFilter === Measures.weight) {
    return "Товары (вес)";
  }

  if (input.filters.measureFilter === Measures.liters) {
    return "Товары (объем)";
  }

  return "Список товаров";
}

export function buildProductsListFilterChips(input: {
  filters: ProductsListFilterState;
  openTypeTag: (tag: string) => void;
  openMeasureFilter: (measure: Measures) => void;
  openPlacementFilter: (placement: ProductPlacementFilter) => void;
  openExpiryFilter: (
    expiry: ProductExpiryFilter,
    expiringDays?: number,
  ) => void;
  openQueryFilter: (query: string) => void;
  openAllProductsList: () => void;
}): ProductsListFilterChip[] {
  const result: ProductsListFilterChip[] = [];

  if (input.filters.placementFilter === "inBox") {
    result.push({
      key: "placement:inBox",
      label: "только в коробках",
      onClick: () => input.openPlacementFilter("inBox"),
    });
  } else if (input.filters.placementFilter === "withoutBox") {
    result.push({
      key: "placement:withoutBox",
      label: "только без коробки",
      onClick: () => input.openPlacementFilter("withoutBox"),
    });
  }

  if (input.filters.expiryFilter === "expiringSoon") {
    result.push({
      key: "expiry:expiringSoon",
      label: `истекают <= ${input.filters.expiringDays} дн.`,
      onClick: () =>
        input.openExpiryFilter("expiringSoon", input.filters.expiringDays),
    });
  } else if (input.filters.expiryFilter === "expired") {
    result.push({
      key: "expiry:expired",
      label: "просроченные",
      onClick: () => input.openExpiryFilter("expired"),
    });
  }

  if (input.filters.measureFilter === Measures.quantity) {
    result.push({
      key: "measure:quantity",
      label: "только количество",
      onClick: () => input.openMeasureFilter(Measures.quantity),
    });
  } else if (input.filters.measureFilter === Measures.weight) {
    result.push({
      key: "measure:weight",
      label: "только вес",
      onClick: () => input.openMeasureFilter(Measures.weight),
    });
  } else if (input.filters.measureFilter === Measures.liters) {
    result.push({
      key: "measure:liters",
      label: "только литры",
      onClick: () => input.openMeasureFilter(Measures.liters),
    });
  }

  if (input.filters.typeTagFilter) {
    result.push({
      key: `typeTag:${input.filters.typeTagFilter}`,
      label: `тег ${formatTypeTag(input.filters.typeTagFilter)}`,
      onClick: () => input.openTypeTag(input.filters.typeTagFilter),
    });
  }

  if (input.filters.queryFilter) {
    result.push({
      key: `q:${input.filters.queryFilter}`,
      label: `поиск: ${input.filters.queryFilter}`,
      onClick: () => input.openQueryFilter(input.filters.queryFilter),
    });
  }

  if (result.length === 0) {
    result.push({
      key: "all",
      label: "без фильтров",
      onClick: input.openAllProductsList,
    });
  }

  return result;
}
